import React from 'react';
import { infoPageStyles as styles } from './sharedStyles';

const DisputeResolution = () => {
  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Dispute Resolution</h1>
        <p style={styles.headerSubtitle}>
          Resolving issues fairly and efficiently
        </p>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Stripe Payment Dispute Protection</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>All payment disputes on Redbird Markets are handled directly by Stripe</strong>, our secure payment processor. 
              This ensures professional, impartial resolution backed by industry-leading payment security standards.
            </p>
          </div>
          <p style={styles.text}>
            When you complete a transaction through our platform, you're protected by Stripe's comprehensive dispute resolution system. 
            Stripe handles all chargebacks, refund requests, and payment disputes according to card network rules and banking regulations.
          </p>
        </div>
        
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>How Stripe Dispute Resolution Works</h2>
          <p style={styles.text}>
            Stripe manages the entire dispute process from start to finish:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Automatic Monitoring:</strong> Stripe automatically detects and tracks all disputes</li>
            <li style={styles.listItem}><strong>Professional Review:</strong> Stripe's fraud prevention team reviews evidence</li>
            <li style={styles.listItem}><strong>Card Network Arbitration:</strong> Disputes follow official Visa, Mastercard, and Amex procedures</li>
            <li style={styles.listItem}><strong>Regulatory Compliance:</strong> All resolutions comply with federal banking regulations</li>
            <li style={styles.listItem}><strong>Transparent Timeline:</strong> Typical resolution within 60-90 days per card network requirements</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Buyer Protection Through Stripe</h2>
          <p style={styles.text}>
            Buyers can file disputes directly with their card issuer for:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Fraudulent Charges:</strong> Unauthorized use of payment card</li>
            <li style={styles.listItem}><strong>Item Not Received:</strong> Product or service not delivered as promised</li>
            <li style={styles.listItem}><strong>Item Significantly Not as Described:</strong> Major discrepancies from listing</li>
            <li style={styles.listItem}><strong>Duplicate Charges:</strong> Charged multiple times for the same transaction</li>
            <li style={styles.listItem}><strong>Processing Errors:</strong> Incorrect charge amount</li>
          </ul>
          <p style={styles.text}>
            <strong>Important:</strong> Buyers must contact their card issuer (bank) within 60-120 days of the transaction date, 
            depending on card network policies. Stripe will automatically receive and process the dispute claim.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>How to File a Payment Dispute</h2>
          
          <div style={{marginBottom: '32px'}}>
            <h3 style={styles.subsectionTitle}>For Buyers: Contact Your Card Issuer</h3>
            <p style={styles.text}>
              To dispute a payment, you must contact your bank or credit card company directly:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Call the customer service number on the back of your card</li>
              <li style={styles.listItem}>Explain the issue and provide transaction details</li>
              <li style={styles.listItem}>Your bank will file a chargeback with Stripe on your behalf</li>
              <li style={styles.listItem}>Stripe automatically handles the dispute process</li>
            </ul>
            <p style={styles.text}>
              <strong>Do this within 60-120 days</strong> of the transaction date, depending on your card network's policy.
            </p>
          </div>

          <div style={{marginBottom: '32px'}}>
            <h3 style={styles.subsectionTitle}>For Sellers: Stripe Notifies You Automatically</h3>
            <p style={styles.text}>
              If a buyer disputes a payment, Stripe will:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Automatically notify you via email about the dispute</li>
              <li style={styles.listItem}>Request evidence to support your case</li>
              <li style={styles.listItem}>Submit your evidence to the card network</li>
              <li style={styles.listItem}>Handle all communication with banks and card networks</li>
            </ul>
            <p style={styles.text}>
              <strong>You'll have 7-14 days</strong> to submit evidence through Stripe's dashboard.
            </p>
          </div>

          <div style={{marginBottom: '32px'}}>
            <h3 style={styles.subsectionTitle}>Dispute Timeline</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}><strong>Day 0:</strong> Buyer files dispute with their bank</li>
              <li style={styles.listItem}><strong>Day 1-2:</strong> Stripe receives dispute and notifies seller</li>
              <li style={styles.listItem}><strong>Day 3-14:</strong> Seller submits evidence to Stripe</li>
              <li style={styles.listItem}><strong>Day 15-30:</strong> Stripe reviews and submits evidence to card network</li>
              <li style={styles.listItem}><strong>Day 30-90:</strong> Card network makes final decision</li>
            </ul>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Evidence Guidelines for Sellers</h2>
          <p style={styles.text}>
            When Stripe notifies you of a dispute, submit compelling evidence through the Stripe Dashboard:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Proof of Delivery:</strong> Photos showing item condition at exchange, timestamp, and buyer receipt</li>
            <li style={styles.listItem}><strong>Communication Records:</strong> Complete message history from Redbird Markets platform</li>
            <li style={styles.listItem}><strong>Original Listing:</strong> Screenshots of your listing description, photos, and price</li>
            <li style={styles.listItem}><strong>Item Description:</strong> Detailed documentation that item matched description</li>
            <li style={styles.listItem}><strong>Meeting Details:</strong> Date, time, location of in-person exchange</li>
            <li style={styles.listItem}><strong>Written Statement:</strong> Clear explanation of what occurred during transaction</li>
          </ul>
          <p style={styles.text}>
            <strong>Important:</strong> Submit all evidence through Stripe's dashboard within the deadline. 
            Late submissions cannot be accepted as they must meet card network filing requirements.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Stripe's Chargeback Protection</h2>
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>Buyers Are Protected By Card Networks For:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Fraudulent transactions (card used without authorization)</li>
              <li style={styles.listItem}>Item not received after payment processed</li>
              <li style={styles.listItem}>Item significantly not as described in listing</li>
              <li style={styles.listItem}>Duplicate charges for same transaction</li>
              <li style={styles.listItem}>Incorrect charge amount</li>
            </ul>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Seller Protection Best Practices</h2>
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>Protect Yourself From Disputes:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}><strong>Accurate Descriptions:</strong> List all flaws, conditions, and specifications clearly</li>
              <li style={styles.listItem}><strong>Clear Photos:</strong> Show item from multiple angles, including any damage</li>
              <li style={styles.listItem}><strong>Document Everything:</strong> Take photos during exchange with buyer present</li>
              <li style={styles.listItem}><strong>Written Confirmation:</strong> Have buyer confirm receipt and satisfaction via message</li>
              <li style={styles.listItem}><strong>Keep Records:</strong> Save all communications and transaction details for 180 days</li>
            </ul>
          </div>
          <p style={styles.text}>
            Following these practices significantly improves your chances of winning disputes, as Stripe and card networks 
            heavily favor sellers with comprehensive documentation.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Dispute Prevention - Critical Guidelines</h2>
          <p style={styles.text}>
            <strong>For Buyers:</strong> Prevent the need to dispute by being thorough:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Inspect items carefully before completing payment</li>
            <li style={styles.listItem}>Test electronics and verify functionality during exchange</li>
            <li style={styles.listItem}>Ask questions about condition, flaws, and specifications before meeting</li>
            <li style={styles.listItem}>Take photos at pickup if item condition is questionable</li>
            <li style={styles.listItem}>Contact seller immediately if issues arise (same day preferred)</li>
          </ul>
          
          <p style={styles.text}>
            <strong>For Sellers:</strong> Protect yourself from chargebacks:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Be 100% honest about item condition - disclose all flaws</li>
            <li style={styles.listItem}>Take timestamped photos during exchange showing item and buyer together</li>
            <li style={styles.listItem}>Have buyer send confirmation message that item was received as described</li>
            <li style={styles.listItem}>Save all platform messages and transaction records for 6 months</li>
            <li style={styles.listItem}>Never encourage buyers to pay outside the platform</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Dispute Outcomes & Consequences</h2>
          <p style={styles.text}>
            Once the card network makes a decision (typically 60-90 days):
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Buyer Wins:</strong> Full refund issued automatically by Stripe, funds reversed from seller</li>
            <li style={styles.listItem}><strong>Seller Wins:</strong> No refund issued, seller keeps payment, dispute fee may be waived</li>
            <li style={styles.listItem}><strong>Dispute Fee:</strong> $15 fee charged to seller for each dispute, regardless of outcome</li>
            <li style={styles.listItem}><strong>Multiple Disputes:</strong> Pattern of lost disputes may result in account restrictions</li>
          </ul>
          <p style={styles.text}>
            <strong>Card network decisions are final.</strong> Neither buyers nor sellers can appeal through Redbird Markets, 
            as the decision is made by Visa, Mastercard, or American Express according to their own rules and regulations.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Redbird Markets's Limited Role</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>Important Notice:</strong> Redbird Markets does not directly handle payment disputes, chargebacks, or refunds. 
              All payment-related disputes are managed exclusively by Stripe and the card networks (Visa, Mastercard, Amex).
            </p>
          </div>
          <p style={styles.text}>
            Redbird Markets's role is limited to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Providing a secure platform for transactions</li>
            <li style={styles.listItem}>Maintaining transaction records and message history</li>
            <li style={styles.listItem}>Offering guidance on best practices and documentation</li>
            <li style={styles.listItem}>Taking action against users who violate platform policies</li>
          </ul>
          <p style={styles.text}>
            For non-payment issues (harassment, prohibited items, policy violations), please <a href="/contact-us" style={styles.link}>contact our support team</a>.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Additional Resources</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Stripe Disputes:</strong> Visit <a href="https://stripe.com/docs/disputes" target="_blank" rel="noopener noreferrer" style={styles.link}>Stripe's Dispute Documentation</a></li>
            <li style={styles.listItem}><strong>Card Network Policies:</strong> Review your card issuer's chargeback rights</li>
            <li style={styles.listItem}><strong>Platform Support:</strong> <a href="/contact-us" style={styles.link}>Contact Redbird Markets Support</a> for platform-related issues</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DisputeResolution;
